import { motion } from "framer-motion";

// Lightweight, reliable SVG wireframe globe with a rotating scan sweep,
// pulsing supply-chain nodes and animated trade arcs. Monochromatic (navy/white).
const NODES = [
  { x: 118, y: 150, label: "US" },
  { x: 204, y: 120, label: "UK" },
  { x: 226, y: 138, label: "EU" },
  { x: 272, y: 268, label: "AU" },
  { x: 300, y: 286, label: "NZ" },
];

const HUB = { x: 250, y: 210 }; // China / sourcing hub

const Globe = () => {
  return (
    <motion.svg
      viewBox="0 0 400 400"
      className="w-full h-full"
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.6, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
      role="img"
      aria-label="Global supply chain network"
    >
      <defs>
        <radialGradient id="glow" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#1e3a6e" stopOpacity="0.55" />
          <stop offset="70%" stopColor="#0a121f" stopOpacity="0.15" />
          <stop offset="100%" stopColor="#050a11" stopOpacity="0" />
        </radialGradient>
        <linearGradient id="sweep" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#5b8def" stopOpacity="0" />
          <stop offset="100%" stopColor="#5b8def" stopOpacity="0.28" />
        </linearGradient>
      </defs>

      <circle cx="200" cy="200" r="185" fill="url(#glow)" />
      <circle cx="200" cy="200" r="160" fill="#0a121f" stroke="rgba(255,255,255,0.14)" strokeWidth="1" />

      {/* rotating scan sweep */}
      <g className="globe-spin" style={{ transformOrigin: "200px 200px" }}>
        <path d="M200 200 L200 40 A160 160 0 0 1 340 130 Z" fill="url(#sweep)" />
      </g>

      {/* latitude lines */}
      {[0, 52, -52, 100, -100].map((dy, i) => {
        const rx = Math.sqrt(160 * 160 - dy * dy);
        return (
          <ellipse
            key={`lat-${i}`}
            cx="200"
            cy={200 + dy}
            rx={rx}
            ry={rx * 0.16}
            fill="none"
            stroke="rgba(255,255,255,0.09)"
            strokeWidth="1"
          />
        );
      })}

      {/* longitude lines */}
      {[160, 118, 66, 18].map((rx, i) => (
        <ellipse
          key={`lon-${i}`}
          cx="200"
          cy="200"
          rx={rx}
          ry="160"
          fill="none"
          stroke="rgba(255,255,255,0.09)"
          strokeWidth="1"
        />
      ))}

      {/* trade arcs from hub to regions */}
      {NODES.map((n, i) => {
        const mx = (HUB.x + n.x) / 2;
        const my = (HUB.y + n.y) / 2 - 60;
        return (
          <path
            key={`arc-${i}`}
            d={`M${HUB.x} ${HUB.y} Q ${mx} ${my} ${n.x} ${n.y}`}
            fill="none"
            stroke="rgba(120,170,255,0.5)"
            strokeWidth="1"
            className="arc-dash"
          />
        );
      })}

      {/* hub */}
      <circle cx={HUB.x} cy={HUB.y} r="4" fill="#fff" />
      <circle cx={HUB.x} cy={HUB.y} r="9" fill="none" stroke="rgba(255,255,255,0.4)" strokeWidth="1" />

      {/* region nodes */}
      {NODES.map((n, i) => (
        <g key={`node-${i}`}>
          <circle
            cx={n.x}
            cy={n.y}
            r="9"
            fill="rgba(91,141,239,0.18)"
            className="pulse-dot"
            style={{ transformOrigin: `${n.x}px ${n.y}px`, animationDelay: `${i * 0.4}s` }}
          />
          <circle cx={n.x} cy={n.y} r="3" fill="#9dc0ff" />
        </g>
      ))}
    </motion.svg>
  );
};

export default Globe;
